import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import PrevButton from "../../../pages/internalPages/Notification/PrevButton";
import { toast } from 'react-toastify';
import { NOTIFICATION_TYPES } from "../../../enums/NotificationData";
import { NotificationToastMessages, toastOptions } from "../../../config/Constants";

export default function Step5({
    goBack,
    saveNotification,
    saveAndSendNotification,
    saveAndScheduleNotification,
    notificationId,
    type,
    notificationItem
}) {
    const [sendOption, setSendOption] = useState('now');

    const discountNotLive = type === NOTIFICATION_TYPES.INSTORE && notificationItem?.action?.isLive === false;
    const discountStartDate = notificationItem?.action?.startDate ? new Date(notificationItem.action.startDate) : null;
    const nowFloor = moment().add(1, 'minute').toDate();
    const minDateTime = discountNotLive && discountStartDate && discountStartDate > nowFloor ? discountStartDate : nowFloor;

    const [scheduledDate, setScheduledDate] = useState(() => (
        discountNotLive && discountStartDate && discountStartDate > nowFloor
            ? discountStartDate
            : moment().add(1, 'hour').toDate()
    ));

    useEffect(() => {
        if (discountNotLive && sendOption === 'now') {
            setSendOption('schedule');
        }
    }, [discountNotLive, sendOption]);

    const handleSendNow = () => {
        if (discountNotLive) {
            toast.error(NotificationToastMessages.discount_not_live_yet, toastOptions);
            return;
        }
        saveAndSendNotification();
    };

    const handleSchedule = () => {
        if (scheduledDate < minDateTime) {
            const message = discountNotLive && discountStartDate
                ? `Scheduled time must be on or after the discount's start date (${moment(discountStartDate).format('MMMM D, yyyy h:mm A')})`
                : 'Scheduled time must be in the future';
            toast.error(message);
            return;
        }

        const sendAt = moment(scheduledDate).toISOString();
        saveAndScheduleNotification(sendAt);
    };

    return (
        <>
            <section>
                <div className="row justify-content-center">
                    <div className="col-lg-6 mb-2">
                        <div className="card border-top shadow">
                            <div className="card-header border-0 pb-0">
                                <h2 className="card-title">Send Options</h2>
                            </div>
                            <div className="card-body">
                                <div className="mb-4">
                                    <label className="form-label fw-bold">When should this notification be sent?</label>

                                    <div className="d-flex flex-column gap-3 mb-3">
                                        <div
                                            className={`form-check p-3 border rounded ${sendOption === 'now' ? 'bg-light border-primary' : ''}`}
                                            style={{ cursor: discountNotLive ? 'not-allowed' : 'pointer', opacity: discountNotLive ? 0.6 : 1 }}
                                            onClick={() => !discountNotLive && setSendOption('now')}
                                        >
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                style={{ marginLeft: 0 }}
                                                name="sendOption"
                                                value="now"
                                                checked={sendOption === 'now'}
                                                disabled={discountNotLive}
                                                onChange={(e) => setSendOption(e.target.value)}
                                                id="sendNow"
                                            />
                                            <label className="form-check-label ms-2" htmlFor="sendNow" style={{ cursor: discountNotLive ? 'not-allowed' : 'pointer' }}>
                                                <strong>Send immediately</strong>
                                                <br />
                                                <small className="text-muted">The notification will be sent right away</small>
                                                {discountNotLive && (
                                                    <>
                                                        <br />
                                                        <small className="text-danger">
                                                            This discount isn't live yet — schedule for on/after its start date instead.
                                                        </small>
                                                    </>
                                                )}
                                            </label>
                                        </div>

                                        <div
                                            className={`form-check p-3 border rounded ${sendOption === 'schedule' ? 'bg-light border-primary' : ''}`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setSendOption('schedule')}
                                        >
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                style={{ marginLeft: 0 }}
                                                name="sendOption"
                                                value="schedule"
                                                checked={sendOption === 'schedule'}
                                                onChange={(e) => setSendOption(e.target.value)}
                                                id="sendSchedule"
                                            />
                                            <label className="form-check-label ms-2" htmlFor="sendSchedule" style={{ cursor: 'pointer' }}>
                                                <strong>Schedule for later</strong>
                                                <br />
                                                <small className="text-muted">Choose a specific date and time to send</small>
                                            </label>
                                        </div>
                                    </div>

                                    {sendOption === 'schedule' && (
                                        <div className="schedule-picker-container mt-3 p-3 bg-light rounded">
                                            <label className="form-label fw-bold">Select Date & Time (UTC)</label>
                                            {discountNotLive && discountStartDate && (
                                                <small className="form-text text-danger d-block mb-2">
                                                    This discount goes live on {moment(discountStartDate).format('MMMM D, yyyy h:mm A')} — you can't schedule before then.
                                                </small>
                                            )}
                                            <DatePicker
                                                selected={scheduledDate}
                                                onChange={(date) => setScheduledDate(date)}
                                                showTimeSelect
                                                timeFormat="HH:mm"
                                                timeIntervals={15}
                                                dateFormat="MMMM d, yyyy h:mm aa"
                                                minDate={minDateTime}
                                                className="form-control"
                                                placeholderText="Select date and time"
                                            />
                                            <small className="form-text text-muted d-block mt-2">
                                                Time is in UTC. Current UTC time: {moment().utc().format('YYYY-MM-DD HH:mm:ss')}
                                            </small>
                                            <small className="form-text text-muted d-block">
                                                Selected time: {moment(scheduledDate).utc().format('YYYY-MM-DD HH:mm:ss')} UTC
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="text-end toolbar toolbar-bottom p-2">
                <PrevButton onClick={goBack} />
                <button
                    className="btn btn-primary sw-btn-next ms-1"
                    onClick={() => saveNotification()}
                >
                    Save as Draft
                </button>
                {!notificationId && (
                    <>
                        {sendOption === 'now' ? (
                            <button
                                type="button"
                                className="btn btn-secondary sw-btn-next ms-1"
                                style={{ backgroundColor: '#0473ae', color: '#fff' }}
                                onClick={handleSendNow}
                            >
                                Send Now
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-success sw-btn-next ms-1"
                                onClick={handleSchedule}
                            >
                                Schedule Notification
                            </button>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
